import axios from "axios";
import { API_BASE } from "../utils/api";

// Recursive AI documentation template section tree.
// A node is either a group (has `sections`) or a leaf (has `type`).
export interface TemplateSectionLeaf {
  key: string;
  title: string;
  type?: "string" | "array";
  description?: string;
}
export interface TemplateSectionGroup {
  key: string;
  title: string;
  sections: TemplateSection[];
  description?: string;
}
export type TemplateSection = TemplateSectionLeaf | TemplateSectionGroup;

export interface TemplateJsonConfig {
  name?: string;
  documentation_type?: string;
  description?: string;
  system_prompt: string;
  sections: TemplateSection[];
}

export interface AdminTemplate {
  id: number;
  name: string;
  documentation_type: string;
  description: string;
  // Doctor.specialization keys this template is visible to (empty = individual-assignment only)
  specialties: string[];
  json_config: TemplateJsonConfig;
  active: boolean;
  assigned_doctor_ids: number[];
  created_at: string;
  updated_at: string;
}

// Mirrors Doctor.SPECIALTY_CHOICES (docfi-db/doctor/models.py) — used for the
// specialty multi-select when authoring a template's visibility.
export const SPECIALTY_CHOICES: { value: string; label: string }[] = [
  { value: "medicine", label: "Medicine" },
  { value: "emergency_medicine", label: "Emergency Medicine" },
  { value: "psychiatry_behavioral_health", label: "Psychiatry & Behavioral Health" },
  { value: "internal_medicine", label: "Internal Medicine" },
  { value: "surgery", label: "Surgery" },
  { value: "pediatrics", label: "Pediatrics" },
  { value: "obstetrics_gynecology", label: "Obstetrics & Gynecology" },
  { value: "radiology", label: "Radiology" },
  { value: "anesthesiology", label: "Anesthesiology" },
  { value: "neurology", label: "Neurology" },
  { value: "ophthalmology", label: "Ophthalmology" },
  { value: "dentistry", label: "Dentistry" },
  { value: "ambulatory_care", label: "Ambulatory Care" },
  { value: "behavioral_health", label: "Behavioral Health" },
  { value: "neurosurgery", label: "Neurosurgery" },
  { value: "orthopedic_surgery", label: "Orthopedic Surgery" },
  { value: "dermatology", label: "Dermatology" },
  { value: "otolaryngology", label: "Otolaryngology/ENT" },
  { value: "virtual_expresscare", label: "Virtual ExpressCare" },
  { value: "child_psychiatry", label: "Child Psychiatry" },
  { value: "chest_medicine", label: "Chest Medicine" },
  { value: "urology", label: "Urology" },
  { value: "pathology_laboratory", label: "Pathology & Laboratory Medicine" },
  { value: "physical_medicine", label: "Physical Medicine & Rehabilitation" },
  { value: "oral_maxillofacial", label: "Oral & Maxillofacial Surgery" },
  { value: "cardiothoracic_surgery", label: "Cardiothoracic Surgery" },
  { value: "podiatry", label: "Podiatry" },
  { value: "cardiology", label: "Cardiology" },
  { value: "radiation_oncology", label: "Radiation Oncology" },
  { value: "family_medicine", label: "Family Medicine" },
  { value: "plastic_surgery", label: "Plastic Surgery" },
  { value: "consultative_services", label: "Consultative Services" },
];

export interface DoctorTemplateAssignment {
  id: number;
  doctor: number;
  doctor_name: string;
  template: number;
  template_name: string;
  is_default: boolean;
  assigned_at: string;
}

export interface DoctorOption {
  doctor_id: number;
  name: string;
  specialization: string;
  specialty_display: string;
}

/** Whether the built-in SOAP entry shows in one doctor's template switcher. */
export interface BuiltinSoapVisibility {
  doctor_id: number;
  name: string;
  enabled: boolean;
}

const adminTemplatesService = {
  async list(): Promise<AdminTemplate[]> {
    const { data } = await axios.get(`${API_BASE}/admin/document-templates/`);
    return data.results ?? data;
  },

  async get(id: number): Promise<AdminTemplate> {
    const { data } = await axios.get(`${API_BASE}/admin/document-templates/${id}/`);
    return data;
  },

  async create(payload: Partial<AdminTemplate>): Promise<AdminTemplate> {
    const { data } = await axios.post(`${API_BASE}/admin/document-templates/`, payload);
    return data;
  },

  async update(id: number, payload: Partial<AdminTemplate>): Promise<AdminTemplate> {
    const { data } = await axios.patch(
      `${API_BASE}/admin/document-templates/${id}/`,
      payload,
    );
    return data;
  },

  async remove(id: number): Promise<void> {
    await axios.delete(`${API_BASE}/admin/document-templates/${id}/`);
  },

  async assignments(templateId: number): Promise<DoctorTemplateAssignment[]> {
    const { data } = await axios.get(
      `${API_BASE}/admin/document-templates/${templateId}/assignments/`,
    );
    return data;
  },

  async assign(
    templateId: number,
    doctorId: number,
    isDefault = false,
  ): Promise<DoctorTemplateAssignment> {
    const { data } = await axios.post(
      `${API_BASE}/admin/document-templates/${templateId}/assign/`,
      { doctor_id: doctorId, is_default: isDefault },
    );
    return data;
  },

  async unassign(templateId: number, doctorId: number): Promise<void> {
    await axios.post(`${API_BASE}/admin/document-templates/${templateId}/unassign/`, {
      doctor_id: doctorId,
    });
  },

  // Stops a malformed or hostile `next` chain from looping forever. Page size
  // is 10, so this is far above any realistic doctor count.
  MAX_DOCTOR_PAGES: 100,

  // Query string of the next page, or null at the end of the list.
  //
  // Only the query is taken from the server's `next` — never its host. DRF
  // builds that URL from the request's Host header, and every request carries
  // the Cognito bearer token, so following it verbatim sends credentials
  // wherever the header happened to point. In practice a dev-pool token was
  // being sent to the production API (403); the same path would hand the token
  // to any host injected via X-Forwarded-Host.
  _nextPageQuery(next: string | null | undefined): string | null {
    if (!next) return null;
    try {
      const params = new URL(next, window.location.origin).searchParams;
      return params.toString() || null;
    } catch {
      return null;
    }
  },

  /**
   * Per-doctor visibility of the built-in "SOAP Note (Default)" entry.
   *
   * The built-in note is not a DocumentTemplate row, so it cannot be assigned
   * like the others — it is on for every doctor until an admin turns it off.
   */
  async builtinSoapVisibility(): Promise<BuiltinSoapVisibility[]> {
    const { data } = await axios.get(
      `${API_BASE}/admin/document-templates/builtin-soap/`,
    );
    return data;
  },

  async setBuiltinSoapVisibility(
    doctorId: number,
    enabled: boolean,
  ): Promise<BuiltinSoapVisibility> {
    const { data } = await axios.post(
      `${API_BASE}/admin/document-templates/builtin-soap/`,
      { doctor_id: doctorId, enabled },
    );
    return data;
  },

  async listDoctors(): Promise<DoctorOption[]> {
    type PagedResponse = { results: DoctorOption[]; next: string | null };
    const results: DoctorOption[] = [];
    let query: string | null = null;

    for (let page = 0; page < adminTemplatesService.MAX_DOCTOR_PAGES; page += 1) {
      const url = `${API_BASE}/doctors/${query ? `?${query}` : ""}`;
      // eslint-disable-next-line no-await-in-loop
      const response: { data: PagedResponse | DoctorOption[] } =
        await axios.get<PagedResponse | DoctorOption[]>(url);
      const payload: PagedResponse | DoctorOption[] = response.data;

      if (Array.isArray(payload)) {
        results.push(...payload);
        return results;
      }
      results.push(...(payload.results ?? []));
      query = adminTemplatesService._nextPageQuery(payload.next);
      if (!query) return results;
    }
    return results;
  },
};

export default adminTemplatesService;
